import { ChangeEvent, cloneElement, DragEvent, isValidElement, ReactElement, ReactNode, useId } from 'react';
import {
  Box,
  Button,
  Checkbox,
  GlobalStyles,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { LoadingButton } from '@mui/lab';
import { ServiceOrIssueStatus } from '../../shared/constants/constants';
import { queryAdderPalette, queryAdderTypeScale } from '../../shared/constants/queryAdderPalette';
import { normalizeServiceId } from '../../shared/utils/serviceLogNormalization';
import { fontWeights, semanticTypography } from '../../theme/typography';
import { appPalette } from '../../theme/palette';
import { getCalendarTypography } from '../Calendar/calendarPalette';
import { calendarSnapshotChipTypography } from '../Calendar/CalendarSnapshotChip';
import { QueryAdderCell } from './QueryAdderCell';
import {
  adderCancelButtonSx,
  adderHeaderActionsSx,
  adderHeaderIconTileSx,
  adderHeaderTitleGroupSx,
  adderHeaderTitleSx,
  adderModalHeaderSx,
  adderPrimaryActionButtonSx,
  adderSectionBodySx,
  adderSectionCardSx,
  adderSectionIconColor,
  adderSectionHeaderSx,
  adderSectionIconSx,
  adderSectionIconTileSx,
  queryAdderPopupActionButtonSx,
  queryAdderPopupEyebrowSx,
  queryAdderTooltipProps,
  queryAdderTooltipTitleSx,
  queryAdderPopupTitleSx,
  topicLinkingPanelSx,
} from './queryAdderPopupStyles';

type QueryAdderUi = Record<string, any>;
type QueryAdderSx = Record<string, any>;
type QueryAdderFields = Record<string, any>;
type DropdownTable = Record<string, any>;
type QueryAdderEventHandler = Record<string, any>;
type QueryAdderCallback = (value: any, field: string) => void;
type QueryAdderDragHandler = (event: DragEvent<any>) => void;
type QueryAdderActionHandler = () => void;
type QueryAdderStatusLabel =
  | typeof ServiceOrIssueStatus.IN_PROGRESS
  | typeof ServiceOrIssueStatus.COMPLETED
  | typeof ServiceOrIssueStatus.PARTICIPATION_RIBBON
  | typeof ServiceOrIssueStatus.CANCELLED;

interface QueryAdderFieldGridItemProps {
  children: ReactNode;
  fullWidth?: boolean;
}

interface QueryAdderReadOnlyFieldProps {
  label: string;
  value?: string | number | null;
  emptyText?: string;
  ui: QueryAdderUi;
  readOnly?: boolean;
  readOnlyLabel?: string;
}

interface QueryAdderUploadDropzoneProps {
  ui: QueryAdderUi;
  description: string;
  children: ReactNode;
  showIcon?: boolean;
  handleDrag?: QueryAdderDragHandler;
  handleDrop?: QueryAdderDragHandler;
  isDragActive?: boolean;
}

interface QueryAdderParentTopicMatchFieldProps {
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  ui: QueryAdderUi;
  label?: string;
  info?: string;
}

interface QueryAdderInheritedParentServiceSectionProps {
  children: ReactNode;
  infoText: string;
  ui: QueryAdderUi;
}

interface QueryAdderTopicLinkingCardProps {
  ui: QueryAdderUi;
  checkboxLabel: string;
  checkboxHelperText?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  selectedServiceTitle?: string;
  selectedServiceId?: string;
  expanded?: boolean;
  onExpandedChange: (expanded: boolean) => void;
  searchField?: ReactNode;
  inheritedFields?: ReactNode;
  summaryBadges?: ReactNode;
  inheritedInfoText?: string;
  eyebrow?: string;
  showOpenInLog?: boolean;
}

interface QueryAdderTopicLinkingToggleRowProps {
  ui: QueryAdderUi;
  checked: boolean;
  checkboxLabel: string;
  checkboxHelperText?: string;
  settingRowLabelId: string;
  settingRowHelperId: string;
  onCheckedChange: (checked: boolean) => void;
}

interface QueryAdderSelectedServiceContextProps {
  ui: QueryAdderUi;
  expanded: boolean;
  selectedServiceTitle?: string;
  selectedServiceId?: string;
  showOpenInLog: boolean;
  summaryBadges?: ReactNode;
  inheritedInfoText?: string;
  searchField?: ReactNode;
  inheritedFields?: ReactNode;
  onExpandedChange: (expanded: boolean) => void;
}

interface QueryAdderUnselectedServiceFieldsProps {
  showExpandedContent: boolean;
  searchField?: ReactNode;
  inheritedFields?: ReactNode;
}

interface QueryAdderScopedStylesOptions {
  preserveFieldChrome?: boolean;
  preserveRichTextChrome?: boolean;
}

interface QueryAdderScopedStylesProps {
  scopeClassName: string;
  ui: QueryAdderUi;
  options?: QueryAdderScopedStylesOptions;
}

interface HoverInfoProps {
  text?: string;
  ui: QueryAdderUi;
}

interface FieldLabelProps extends HoverInfoProps {
  label: string;
  required?: boolean;
  info?: string;
  showInfoIcon?: boolean;
  tooltipOnLabel?: boolean;
}

interface SectionCardProps {
  title: ReactNode;
  icon: ReactNode;
  children: ReactNode;
  ui: QueryAdderUi;
  iconBg?: string;
  iconColor?: string;
}

const getSectionIconSx = (iconSx?: QueryAdderSx) => {
  if (!iconSx) return adderSectionIconSx;
  return { ...adderSectionIconSx, ...iconSx };
};

interface StatusPillProps {
  label: QueryAdderStatusLabel;
  active: boolean;
  onClick: QueryAdderActionHandler;
  ui: QueryAdderUi;
}

interface QueryAdderActionProps {
  onSubmit: QueryAdderActionHandler;
  onCancel: QueryAdderActionHandler;
  onDelete?: QueryAdderActionHandler;
  isUpdatingItem?: boolean;
  isUpdatingRow?: boolean;
  primaryColor?: string;
  primaryTextColor?: string;
}

interface QueryAdderHeaderProps extends QueryAdderActionProps {
  title: string;
  infoText?: string;
  icon: ReactNode;
  ui: QueryAdderUi;
}

interface QueryAdderStickyHeaderProps {
  title: string;
  infoText?: string;
  icon: ReactNode;
  ui: QueryAdderUi;
  onClose?: QueryAdderActionHandler;
  onSubmit?: QueryAdderActionHandler;
  onCancel?: QueryAdderActionHandler;
  onDelete?: QueryAdderActionHandler;
  isUpdatingItem?: boolean;
  isUpdatingRow?: boolean;
  primaryColor?: string;
  primaryTextColor?: string;
  titleSx?: QueryAdderSx;
  iconCardSx?: QueryAdderSx;
  containerSx?: QueryAdderSx;
  topBorderColor?: string;
}

interface QueryAdderModalShellProps {
  children: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  bodySx?: QueryAdderSx;
  contentSx?: QueryAdderSx;
}

interface QueryAdderLayoutSkeletonProps {
  ui: QueryAdderUi;
  detailRows?: number;
  materialRows?: number;
}

interface QueryAdderFooterActionsProps extends QueryAdderActionProps {
  ui: QueryAdderUi;
}

interface QueryAdderIconFieldProps {
  icon: ReactNode;
  children: ReactNode;
  color: string;
  backgroundColor: string;
  borderColor?: string;
  iconOffsetTop?: number;
}

export interface RenderQueryAdderCellArgs {
  col: string;
  fields: QueryAdderFields;
  dropdownTable: DropdownTable;
  callback: QueryAdderCallback;
  currMonth?: string | number | Date;
  requiredInputs: string[];
  errorCols: string[];
  eventHandler: QueryAdderEventHandler;
  files?: any[];
  handleDrag?: QueryAdderDragHandler;
  handleDrop?: QueryAdderDragHandler;
  isDragActive?: boolean;
  isUpdatingRow?: boolean;
  hideLabel?: boolean;
  labelText?: string;
  fullWidthContainer?: boolean;
  placeholderText?: string;
  autoFocusLinkedService?: boolean;
  sectionEyebrowSx?: QueryAdderSx;
  sectionSubtitleSx?: QueryAdderSx;
  sectionTitleText?: string;
  fileRenderMode?: string;
  liveLinkRenderMode?: string;
}

const AnyQueryAdderCell = QueryAdderCell as any;

export const queryAdderTwoColumnGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
  columnGap: 2,
  rowGap: 3.25,
};

export const queryAdderWideTwoColumnGridSx = {
  ...queryAdderTwoColumnGridSx,
  columnGap: 5,
  rowGap: 3.75,
};

export const queryAdderOneColumnFieldGridSx = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: queryAdderTwoColumnGridSx.rowGap,
};

export const queryAdderFullWidthGridColumnSx = { xs: 'span 1', lg: 'span 2' };

export const queryAdderFooterActionSx = {
  ...queryAdderPopupActionButtonSx,
  minWidth: 108,
  fontWeight: 700,
};

const queryAdderSectionHeaderTitleSx = {
  ...queryAdderPopupEyebrowSx,
  mt: 0,
  fontSize: '12.5px',
  fontWeight: 600,
  textTransform: 'none',
  letterSpacing: '0.01em',
  lineHeight: 1.25,
};

export function QueryAdderFieldGridItem({ children, fullWidth = false }: Readonly<QueryAdderFieldGridItemProps>) {
  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        gridColumn: fullWidth ? queryAdderFullWidthGridColumnSx : 'auto',
        px: { xs: 0, sm: 0.75 },
      }}
    >
      {children}
    </Box>
  );
}

export function QueryAdderReadOnlyField({
  label,
  value,
  emptyText = 'Not set',
  ui,
  readOnly = false,
  readOnlyLabel = 'Read only',
}: Readonly<QueryAdderReadOnlyFieldProps>) {
  const theme = useTheme();
  const sharedTypography = getQueryAdderSharedTypography(theme);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ ...sharedTypography.fieldLabel, mb: 1, color: ui.title }}>{label}</Typography>
      <Box
        sx={{
          minHeight: 40,
          border: `1px solid ${ui.inputBorder}`,
          borderRadius: '12px',
          px: 1.75,
          py: 1.25,
          backgroundColor: readOnly ? ui.softGray || alpha(ui.inputBorder, 0.12) : ui.white,
          borderColor: readOnly ? ui.divider || ui.inputBorder : ui.inputBorder,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: value ? ui.text : ui.muted, flex: 1 }}>
          {value || emptyText}
        </Typography>
        {readOnly ? (
          <Typography
            component="span"
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: ui.helper,
              border: `1px solid ${ui.divider || ui.inputBorder}`,
              borderRadius: '999px',
              px: 1,
              py: 0.5,
              whiteSpace: 'nowrap',
              backgroundColor: ui.white,
            }}
          >
            {readOnlyLabel}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export const getQueryAdderSectionEyebrowSx = (ui: QueryAdderUi, color = ui.main) => ({
  ...queryAdderPopupEyebrowSx,
  fontFamily: queryAdderPopupTitleSx.fontFamily,
  fontWeight: 600,
  letterSpacing: '0.08em',
  color,
  mb: 0.5,
});

export const getQueryAdderSectionSubtitleSx = (ui: QueryAdderUi, sharedTypography: QueryAdderSx = {}) => ({
  ...sharedTypography,
  fontSize: queryAdderTypeScale.helperText,
  lineHeight: 1.5,
  color: ui.helper,
});

export function QueryAdderUploadDropzone({
  ui,
  description,
  children,
  showIcon = true,
  handleDrag,
  handleDrop,
  isDragActive = false,
}: Readonly<QueryAdderUploadDropzoneProps>) {
  return (
    <Box
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      sx={{
        border: `1px dashed ${ui.uploadBorder}`,
        borderRadius: '18px',
        borderColor: isDragActive ? ui.main : ui.uploadBorder,
        backgroundColor: isDragActive ? alpha(ui.main, 0.1) : ui.uploadSurface,
        minHeight: 214,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 4,
      }}
    >
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        {showIcon ? <CloudUploadOutlinedIcon sx={{ fontSize: 22, color: alpha(ui.main, 0.72), mb: 1 }} /> : null}
        <Typography variant="subtitle2" sx={{ fontSize: '13px', fontWeight: 500, mb: 0.75 }}>
          Drag and drop files here
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '12px', color: ui.helper, mb: 2.5, lineHeight: 1.45 }}>
          {description}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>{children}</Box>
      </Box>
    </Box>
  );
}

export function QueryAdderParentTopicMatchField({
  checked,
  onChange,
  ui,
  label = 'Match parent Topic for analytics',
  info = 'Keep this matched to the parent Service when you want related entries counted together in analytics.',
}: Readonly<QueryAdderParentTopicMatchFieldProps>) {
  return (
    <QueryAdderFieldGridItem>
      <FieldLabel label={label} info={info} ui={ui} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 40,
          border: `1px solid ${ui.service}`,
          borderRadius: '12px',
          px: 1.5,
          backgroundColor: ui.white,
        }}
      >
        <Checkbox checked={checked} onChange={onChange} sx={{ p: 0.5, mr: 1, color: ui.service }} />
        <Typography variant="body2" sx={{ color: ui.text }}>
          {label}
        </Typography>
      </Box>
    </QueryAdderFieldGridItem>
  );
}

export function QueryAdderInheritedParentServiceSection({
  children,
  infoText,
  ui,
}: Readonly<QueryAdderInheritedParentServiceSectionProps>) {
  return (
    <Box sx={{ p: 0 }}>
      <Box sx={queryAdderTwoColumnGridSx}>{children}</Box>
    </Box>
  );
}

function QueryAdderTopicLinkingToggleRow({
  ui,
  checked,
  checkboxLabel,
  checkboxHelperText,
  settingRowLabelId,
  settingRowHelperId,
  onCheckedChange,
}: Readonly<QueryAdderTopicLinkingToggleRowProps>) {
  const helperId = checkboxHelperText ? settingRowHelperId : undefined;
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onCheckedChange(!checked);
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onCheckedChange(!checked)}
      onKeyDown={handleKeyDown}
      aria-labelledby={settingRowLabelId}
      aria-describedby={helperId}
      sx={{
        ...topicLinkingPanelSx({
          active: checked,
          ui,
        }),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, pr: 1 }}>
          <Typography
            id={settingRowLabelId}
            variant="body2"
            sx={{
              color: ui.text,
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0.03em',
              fontFamily: semanticTypography.fieldLabel.fontFamily,
            }}
          >
            {checkboxLabel}
          </Typography>
        </Stack>
        {checkboxHelperText ? (
          <Typography id={settingRowHelperId} variant="body2" sx={{ mt: 0.5, color: ui.helper, lineHeight: 1.45 }}>
            {checkboxHelperText}
          </Typography>
        ) : null}
      </Box>
      <Switch
        checked={checked}
        onChange={(event) => onCheckedChange(event.target.checked)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        inputProps={{
          'aria-labelledby': settingRowLabelId,
          'aria-describedby': helperId,
        }}
        sx={{
          flexShrink: 0,
          '& .MuiSwitch-switchBase': {
            color: '#BDBDBD',
          },
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: appPalette.primary.main,
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: alpha(appPalette.primary.main, 0.34),
            opacity: 1,
          },
          '& .MuiSwitch-track': {
            backgroundColor: '#E0E0E0',
            opacity: 1,
          },
        }}
      />
    </Box>
  );
}

function QueryAdderSelectedServiceContext({
  ui,
  expanded,
  selectedServiceTitle,
  selectedServiceId,
  showOpenInLog,
  summaryBadges,
  inheritedInfoText,
  searchField,
  inheritedFields,
  onExpandedChange,
}: Readonly<QueryAdderSelectedServiceContextProps>) {
  const hasSelectedService = Boolean(selectedServiceTitle);
  const normalizedSelectedServiceId = normalizeServiceId(selectedServiceId);
  if (!hasSelectedService) return null;

  return (
    <Box sx={{ border: `1px solid ${ui.divider}`, borderRadius: '16px', backgroundColor: ui.softGray, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center" flexWrap="nowrap">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {expanded ? (
            searchField
          ) : (
            <Typography
              sx={{
                ...semanticTypography.fieldLabel,
                fontSize: queryAdderTypeScale.fieldLabel,
                color: ui.title,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedServiceTitle}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap" justifyContent="flex-end">
          {showOpenInLog && normalizedSelectedServiceId ? (
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 15.5 }} />}
              onClick={() =>
                window.open(
                  `/service-log/${encodeURIComponent(normalizedSelectedServiceId)}`,
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              sx={{
                height: 30,
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '12.5px',
                fontWeight: 600,
                fontFamily: queryAdderPopupTitleSx.fontFamily,
                px: 1.25,
                minWidth: 'auto',
                backgroundColor: 'transparent',
                borderColor: 'transparent',
                color: ui.title,
                '&:hover': {
                  backgroundColor: '#F5F7FA',
                  borderColor: 'transparent',
                },
              }}
            >
              Open Service
            </Button>
          ) : null}
          <Button
            variant="text"
            size="small"
            onClick={() => onExpandedChange(!expanded)}
            endIcon={expanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
            aria-label={expanded ? 'Collapse section' : 'Expand section'}
            sx={{
              height: 30,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '12.5px',
              fontWeight: 600,
              fontFamily: queryAdderPopupTitleSx.fontFamily,
              px: expanded ? 0.5 : 1.25,
              minWidth: 'auto',
              color: ui.title,
              '&:hover': { backgroundColor: '#F5F7FA' },
            }}
          >
            {expanded ? '' : 'Expand'}
          </Button>
        </Stack>
      </Stack>
      {expanded && summaryBadges ? <Box sx={{ mt: 1 }}>{summaryBadges}</Box> : null}

      {expanded ? (
        <Stack
          spacing={2}
          sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${ui.divider}`,
          }}
        >
          {inheritedFields}
        </Stack>
      ) : null}
    </Box>
  );
}

function QueryAdderUnselectedServiceFields({
  showExpandedContent,
  searchField,
  inheritedFields,
}: Readonly<QueryAdderUnselectedServiceFieldsProps>) {
  if (!showExpandedContent) return null;

  return (
    <Stack spacing={2}>
      {searchField}
      {inheritedFields}
    </Stack>
  );
}

export function QueryAdderTopicLinkingCard({
  ui,
  checkboxLabel,
  checkboxHelperText,
  checked,
  onCheckedChange,
  selectedServiceTitle,
  selectedServiceId,
  expanded = true,
  onExpandedChange,
  searchField,
  inheritedFields,
  summaryBadges,
  inheritedInfoText,
  eyebrow = 'Topic Linking',
  showOpenInLog = true,
}: Readonly<QueryAdderTopicLinkingCardProps>) {
  const settingRowLabelId = useId();
  const settingRowHelperId = useId();
  const hasSelectedService = Boolean(selectedServiceTitle);
  const showExpandedContent = checked && (!hasSelectedService || expanded);

  return (
    <Box sx={adderSectionCardSx(ui)}>
      <Stack direction="row" spacing={2} alignItems="center" sx={adderSectionHeaderSx(ui)}>
        <Box sx={adderSectionIconTileSx}>
          <AccountTreeOutlinedIcon sx={adderSectionIconSx} />
        </Box>
        <Typography
          sx={{
            ...queryAdderSectionHeaderTitleSx,
            color: ui.title,
          }}
        >
          {eyebrow}
        </Typography>
      </Stack>

      <Box sx={{ ...adderSectionBodySx, py: 1.75 }}>
        <QueryAdderTopicLinkingToggleRow
          ui={ui}
          checked={checked}
          checkboxLabel={checkboxLabel}
          checkboxHelperText={checkboxHelperText}
          settingRowLabelId={settingRowLabelId}
          settingRowHelperId={settingRowHelperId}
          onCheckedChange={onCheckedChange}
        />

        {checked ? (
          <Box sx={{ mt: 2 }}>
            <QueryAdderSelectedServiceContext
              ui={ui}
              expanded={expanded}
              selectedServiceTitle={selectedServiceTitle}
              selectedServiceId={selectedServiceId}
              showOpenInLog={showOpenInLog}
              summaryBadges={summaryBadges}
              inheritedInfoText={inheritedInfoText}
              searchField={searchField}
              inheritedFields={inheritedFields}
              onExpandedChange={onExpandedChange}
            />
            <QueryAdderUnselectedServiceFields
              showExpandedContent={!hasSelectedService && showExpandedContent}
              searchField={searchField}
              inheritedFields={inheritedFields}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export function getQueryAdderSharedTypography(theme: Theme) {
  const calendarTypography = getCalendarTypography(theme);
  const queryAdderModalChipFontSize = `calc(${calendarSnapshotChipTypography.fontSize} + 2pt)`;

  return {
    body: {
      ...semanticTypography.modalBody,
      color: queryAdderPalette.common.title,
    },
    fieldLabel: {
      ...semanticTypography.fieldLabel,
      fontSize: queryAdderTypeScale.fieldLabel,
    },
    chipLabel: {
      ...calendarSnapshotChipTypography,
      fontSize: queryAdderModalChipFontSize,
      fontWeight: fontWeights.medium,
      textTransform: 'none',
    },
    title: calendarTypography.title,
    sectionTitle: calendarTypography.sectionTitle,
    eyebrow: calendarTypography.eyebrowCompact,
  };
}

export function buildQueryAdderGlobalStyles(
  scopeClassName: string,
  ui: QueryAdderUi,
  sharedTypography: QueryAdderSx,
  options: QueryAdderScopedStylesOptions = {}
) {
  const { preserveFieldChrome = false, preserveRichTextChrome = false } = options;
  const queryAdderModalChipFontSize = `calc(${calendarSnapshotChipTypography.fontSize} + 2pt)`;

  return {
    [`${scopeClassName}, ${scopeClassName} *`]: {
      fontFamily: sharedTypography.body.fontFamily,
    },
    [`${scopeClassName} h1, ${scopeClassName} h2, ${scopeClassName} h3, ${scopeClassName} h4, ${scopeClassName} h5, ${scopeClassName} h6`]:
      {
        fontFamily: sharedTypography.title.fontFamily,
      },
    [`${scopeClassName} .react-datepicker-wrapper, ${scopeClassName} .react-datepicker__input-container`]: {
      width: '100%',
    },
    [`${scopeClassName} .MuiInputBase-root`]: preserveFieldChrome
      ? {
          fontFamily: sharedTypography.body.fontFamily,
        }
      : {
          fontFamily: sharedTypography.body.fontFamily,
          borderRadius: '12px !important',
          backgroundColor: ui.white,
        },
    [`${scopeClassName} .MuiInputBase-input`]: {
      ...sharedTypography.body,
      fontFamily: sharedTypography.body.fontFamily,
      fontSize: queryAdderTypeScale.bodyText,
    },
    [`${scopeClassName} .MuiOutlinedInput-notchedOutline`]: preserveFieldChrome
      ? {}
      : {
          borderColor: `${ui.inputBorder} !important`,
        },
    [`${scopeClassName} .MuiInputAdornment-positionStart svg`]: {
      color: `${adderSectionIconColor} !important`,
      fontSize: '16px !important',
    },
    [`${scopeClassName} .MuiInputAdornment-positionEnd svg`]: {
      color: ui.fieldIcon || ui.muted,
      fontSize: '16px !important',
    },
    [`${scopeClassName} .MuiAutocomplete-tag`]: {
      fontFamily: calendarSnapshotChipTypography.fontFamily,
      fontSize: queryAdderModalChipFontSize,
      fontWeight: sharedTypography.chipLabel.fontWeight,
      lineHeight: calendarSnapshotChipTypography.lineHeight,
      letterSpacing: calendarSnapshotChipTypography.letterSpacing,
      borderRadius: '10px !important',
      backgroundColor: `${ui.tagSurface} !important`,
      border: `1px solid ${ui.tagBorder} !important`,
      color: `${ui.text} !important`,
      minHeight: '26px !important',
      height: 'auto !important',
      padding: '0 2px !important',
    },
    [`${scopeClassName} .MuiAutocomplete-tag .MuiChip-label`]: {
      ...sharedTypography.chipLabel,
      display: 'inline-flex',
      alignItems: 'center',
      paddingLeft: '8px !important',
      paddingRight: '8px !important',
    },
    [`${scopeClassName} .MuiAutocomplete-tag.query-adder-colored-tag`]: {
      backgroundColor: 'var(--query-adder-tag-bg) !important',
      border: 'var(--query-adder-tag-border) !important',
      borderLeft: 'var(--query-adder-tag-border-left, var(--query-adder-tag-border)) !important',
      color: 'var(--query-adder-tag-text) !important',
    },
    [`${scopeClassName} .MuiAutocomplete-tag.query-adder-colored-tag .MuiChip-label`]: {
      color: 'var(--query-adder-tag-text) !important',
    },
    [`${scopeClassName} .MuiAutocomplete-tag .MuiChip-deleteIcon`]: {
      color: `${ui.tagBorder} !important`,
      backgroundColor: 'transparent !important',
      borderRadius: '0 !important',
      width: '16px !important',
      height: '16px !important',
      marginLeft: '2px !important',
      marginRight: '0 !important',
      padding: '0 !important',
    },
    [`${scopeClassName} .MuiAutocomplete-tag .MuiChip-deleteIcon:hover`]: {
      color: `${ui.tagBorder} !important`,
      backgroundColor: 'transparent !important',
    },
    [`${scopeClassName} .MuiAutocomplete-tag.query-adder-colored-tag .MuiChip-deleteIcon`]: {
      color: 'var(--query-adder-tag-delete-color, var(--query-adder-tag-text)) !important',
      backgroundColor: 'transparent !important',
    },
    [`${scopeClassName} .MuiAutocomplete-tag.query-adder-colored-tag .MuiChip-deleteIcon:hover`]: {
      color: 'var(--query-adder-tag-delete-color, var(--query-adder-tag-text)) !important',
      backgroundColor: 'transparent !important',
    },
    [`${scopeClassName} .MuiFormHelperText-root`]: {
      marginLeft: '10px !important',
    },
    [`${scopeClassName} .ql-editor, ${scopeClassName} .ql-toolbar`]: {
      fontFamily: sharedTypography.body.fontFamily,
    },
    [`${scopeClassName} .ql-toolbar`]: preserveRichTextChrome
      ? {}
      : {
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
        },
    [`${scopeClassName} .ql-container`]: preserveRichTextChrome
      ? {
          fontSize: queryAdderTypeScale.helperText,
          resize: 'vertical',
          overflow: 'auto',
          minHeight: '90px',
        }
      : {
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
          fontSize: queryAdderTypeScale.helperText,
          resize: 'vertical',
          overflow: 'auto',
          minHeight: '90px',
        },
    [`${scopeClassName} .ql-editor`]: {
      minHeight: '70px',
      fontSize: queryAdderTypeScale.helperText,
      lineHeight: 1.4,
    },
    [`${scopeClassName} .ql-editor p`]: {
      margin: 0,
      marginBottom: '0.75em',
    },
    [`${scopeClassName} .ql-editor p:last-child`]: {
      marginBottom: 0,
    },
  };
}

export function QueryAdderScopedStyles({ scopeClassName, ui, options }: Readonly<QueryAdderScopedStylesProps>) {
  const theme = useTheme();
  const sharedTypography = getQueryAdderSharedTypography(theme);

  return <GlobalStyles styles={buildQueryAdderGlobalStyles(scopeClassName, ui, sharedTypography, options)} />;
}

QueryAdderScopedStyles.propTypes = {
  scopeClassName: PropTypes.string.isRequired,
  ui: PropTypes.object.isRequired,
  options: PropTypes.shape({
    preserveFieldChrome: PropTypes.bool,
    preserveRichTextChrome: PropTypes.bool,
  }),
};

QueryAdderFieldGridItem.propTypes = {
  children: PropTypes.node.isRequired,
  fullWidth: PropTypes.bool,
};

QueryAdderReadOnlyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  emptyText: PropTypes.string,
  ui: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  readOnlyLabel: PropTypes.string,
};

QueryAdderUploadDropzone.propTypes = {
  ui: PropTypes.object.isRequired,
  description: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  showIcon: PropTypes.bool,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  isDragActive: PropTypes.bool,
};

QueryAdderParentTopicMatchField.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  ui: PropTypes.object.isRequired,
  label: PropTypes.string,
  info: PropTypes.string,
};

QueryAdderInheritedParentServiceSection.propTypes = {
  children: PropTypes.node.isRequired,
  infoText: PropTypes.string.isRequired,
  ui: PropTypes.object.isRequired,
};

QueryAdderTopicLinkingCard.propTypes = {
  ui: PropTypes.object.isRequired,
  checkboxLabel: PropTypes.string.isRequired,
  checkboxHelperText: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
  selectedServiceTitle: PropTypes.string,
  selectedServiceId: PropTypes.string,
  expanded: PropTypes.bool,
  onExpandedChange: PropTypes.func.isRequired,
  searchField: PropTypes.node,
  inheritedFields: PropTypes.node,
  summaryBadges: PropTypes.node,
  inheritedInfoText: PropTypes.string,
  eyebrow: PropTypes.string,
  showOpenInLog: PropTypes.bool,
};

export function HoverInfo({ text, ui }: Readonly<HoverInfoProps>) {
  if (!text) return null;

  return (
    <Tooltip title={<Typography sx={queryAdderTooltipTitleSx}>{text}</Typography>} {...queryAdderTooltipProps}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: ui.muted,
          lineHeight: 1,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
      </Box>
    </Tooltip>
  );
}

export function FieldLabel({ label, required = false, info, ui, showInfoIcon = true }: Readonly<FieldLabelProps>) {
  const theme = useTheme();
  const sharedTypography = getQueryAdderSharedTypography(theme);
  const shouldShowInfoIcon = showInfoIcon;

  const labelContent = (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.65 }}>
      <Typography sx={{ ...sharedTypography.fieldLabel, color: ui.title }}>
        {label}
        {required ? (
          <Box component="span" sx={{ color: ui.accentDark }}>
            {' '}
            *
          </Box>
        ) : null}
      </Typography>
      {shouldShowInfoIcon ? <HoverInfo text={info} ui={ui} /> : null}
    </Stack>
  );

  return labelContent;
}

export function SectionCard({ title, icon, children, ui, iconBg, iconColor }: Readonly<SectionCardProps>) {
  return (
    <Box sx={adderSectionCardSx(ui)}>
      <Stack direction="row" spacing={2} alignItems="center" sx={adderSectionHeaderSx(ui)}>
        <Box
          sx={{
            ...adderSectionIconTileSx,
            backgroundColor: iconBg || adderSectionIconTileSx.backgroundColor,
            color: iconColor || adderSectionIconTileSx.color,
          }}
        >
          {isValidElement(icon) ? cloneElement(icon, { sx: getSectionIconSx(icon.props?.sx) }) : icon}
        </Box>
        {typeof title === 'string' ? (
          <Typography sx={{ ...queryAdderSectionHeaderTitleSx, color: ui.title }}>{title}</Typography>
        ) : (
          title
        )}
      </Stack>
      <Box sx={adderSectionBodySx}>{children}</Box>
    </Box>
  );
}

export function StatusPill({ label, active, onClick, ui }: Readonly<StatusPillProps>) {
  const activeStyles = {
    [ServiceOrIssueStatus.IN_PROGRESS]: {
      backgroundColor: queryAdderPalette.status.inProgress,
      borderColor: queryAdderPalette.status.inProgress,
      color: ui.text,
    },
    [ServiceOrIssueStatus.COMPLETED]: {
      backgroundColor: queryAdderPalette.status.completed,
      borderColor: queryAdderPalette.status.completed,
      color: ui.text,
    },
    [ServiceOrIssueStatus.PARTICIPATION_RIBBON]: {
      backgroundColor: queryAdderPalette.status.participation,
      borderColor: queryAdderPalette.status.participation,
      color: ui.text,
    },
    [ServiceOrIssueStatus.CANCELLED]: {
      backgroundColor: queryAdderPalette.status.cancelled,
      borderColor: queryAdderPalette.status.cancelled,
      color: ui.text,
    },
  };

  const inactiveStyle = {
    backgroundColor: ui.inactivePillSurface,
    borderColor: ui.inactivePillBorder,
    color: ui.inactivePillText,
  };

  const style = active ? activeStyles[label] : inactiveStyle;

  return (
    <Button
      data-cy={`status-${label}`}
      onClick={onClick}
      variant="outlined"
      sx={{
        minWidth: 'auto',
        minHeight: 26,
        height: 'auto',
        px: 1.1,
        borderRadius: '10px',
        textTransform: 'none',
        fontSize: `calc(${calendarSnapshotChipTypography.fontSize} + 2pt)`,
        fontWeight: calendarSnapshotChipTypography.fontWeight,
        lineHeight: calendarSnapshotChipTypography.lineHeight,
        letterSpacing: calendarSnapshotChipTypography.letterSpacing,
        fontFamily: calendarSnapshotChipTypography.fontFamily,
        ...style,
        '&:hover': style,
      }}
    >
      {label === ServiceOrIssueStatus.PARTICIPATION_RIBBON ? 'Participation' : label}
    </Button>
  );
}

export function QueryAdderHeader({
  title,
  infoText,
  icon,
  ui,
  onSubmit,
  onCancel,
  isUpdatingItem = false,
  isUpdatingRow = false,
  primaryColor,
  primaryTextColor,
}: Readonly<QueryAdderHeaderProps>) {
  const theme = useTheme();
  const sharedTypography = getQueryAdderSharedTypography(theme);
  const primaryActionColor = primaryColor || ui.actionPrimary || ui.main;
  const cancelTextColor = ui.main;
  const cancelHoverColor = '#FAFAFA';

  return (
    <Box sx={{ borderBottom: `1px solid ${ui.headerBorder}`, px: 3, py: 1, backgroundColor: ui.white }}>
      <Stack direction="row" justifyContent="space-between" spacing={3} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: ui.softGray,
              color: ui.muted,
              flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ maxWidth: 760, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="h5"
                sx={{
                  ...sharedTypography.title,
                  color: '#000000',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography>
              <HoverInfo text={infoText} ui={ui} />
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'nowrap' }}>
          <LoadingButton
            loading={isUpdatingItem}
            variant="contained"
            onClick={onSubmit}
            startIcon={isUpdatingRow ? <SaveIcon /> : <AddRoundedIcon />}
            sx={{
              minWidth: 116,
              height: 34,
              borderRadius: '10px',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 700,
              px: 1.5,
              boxShadow: '0 1px 2px rgba(16,24,40,0.10)',
              backgroundColor: primaryActionColor,
              color: primaryTextColor || ui.white,
              '&:hover': { backgroundColor: '#155BB5' },
              '& .MuiButton-startIcon svg': { fontSize: 16 },
            }}
            data-cy={isUpdatingRow ? 'update-button-QueryAdder' : 'submit-button-QueryAdder'}
          >
            {isUpdatingRow ? 'Save' : 'Add'}
          </LoadingButton>
          <Button
            variant="outlined"
            onClick={onCancel}
            startIcon={<CloseIcon />}
            sx={{
              minWidth: 116,
              height: 34,
              borderRadius: '10px',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 600,
              px: 1.5,
              borderColor: ui.actionBorder,
              color: cancelTextColor,
              backgroundColor: ui.white,
              boxShadow: '0 1px 2px rgba(16,24,40,0.08)',
              '&:hover': {
                borderColor: ui.actionBorder,
                backgroundColor: cancelHoverColor,
              },
              '& .MuiButton-startIcon svg': { fontSize: 16 },
            }}
            data-cy="cancel-button-QueryAdder"
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export function QueryAdderModalShell({
  children,
  header,
  footer,
  bodySx,
  contentSx,
}: Readonly<QueryAdderModalShellProps>) {
  let renderedHeader = header;

  if (isValidElement(header) && isValidElement(footer)) {
    const footerElement = footer as ReactElement<Record<string, any>>;
    const headerElement = header as ReactElement<Record<string, any>>;
    const footerProps = footerElement.props || {};
    const headerProps = headerElement.props || {};

    const inheritedHeaderActionProps = {
      onSubmit: footerProps.onSubmit,
      onCancel: footerProps.onCancel,
      onDelete: footerProps.onDelete,
      isUpdatingItem: footerProps.isUpdatingItem,
      isUpdatingRow: footerProps.isUpdatingRow,
    };

    const headerActionProps = Object.fromEntries(
      Object.entries(inheritedHeaderActionProps).filter(
        ([key, value]) => headerProps[key] === undefined && value !== undefined
      )
    );

    if (Object.keys(headerActionProps).length > 0) {
      renderedHeader = cloneElement(headerElement, headerActionProps);
    }
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {renderedHeader}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', ...bodySx }}>
        <Box sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: { xs: 2, sm: 3.5 }, pt: 3, pb: 3, ...contentSx }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function QueryAdderSkeletonField({ ui }: Readonly<{ ui: QueryAdderUi }>) {
  return (
    <Box>
      <Skeleton variant="text" width="34%" height={18} sx={{ mb: 0.75, bgcolor: alpha(ui.main, 0.08) }} />
      <Skeleton
        variant="rounded"
        width="100%"
        height={44}
        sx={{ borderRadius: '10px', bgcolor: alpha(ui.main, 0.07) }}
      />
    </Box>
  );
}

const topicLinkingSkeletonFieldKeys = [
  'topic-linking-inherited-topic',
  'topic-linking-inherited-service',
  'topic-linking-inherited-status',
  'topic-linking-inherited-owner',
];

function getSkeletonFieldKeys(prefix: string, count: number) {
  return Array.from({ length: count }, (_value, index) => `${prefix}-${index + 1}`);
}

function QueryAdderTopicLinkingSkeleton({ ui }: Readonly<{ ui: QueryAdderUi }>) {
  const serviceColor = ui.service || queryAdderPalette.service.main;
  const serviceTile = ui.serviceTile || queryAdderPalette.service.tile;
  const settingRowBorderColor = alpha(serviceColor, 0.26);
  const settingRowSurface = alpha(serviceColor, 0.08);

  return (
    <Box
      sx={{
        border: `1px solid ${ui.cardBorder}`,
        borderRadius: '18px',
        backgroundColor: ui.white,
        boxShadow: ui.sectionShadow || '0 1px 2px rgba(16,62,122,0.04)',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ px: 3, py: 1.5, borderBottom: `1px solid ${ui.divider}` }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: serviceTile,
            color: queryAdderPalette.service.main,
            flexShrink: 0,
          }}
        >
          <AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Typography sx={{ ...queryAdderSectionHeaderTitleSx, color: ui.title }}>Topic Linking</Typography>
      </Stack>

      <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            border: `1px solid ${settingRowBorderColor}`,
            backgroundColor: settingRowSurface,
            borderRadius: '12px',
            px: 2.25,
            py: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Skeleton variant="text" width={210} height={22} sx={{ bgcolor: alpha(serviceColor, 0.1) }} />
            <Skeleton variant="text" width="58%" height={18} sx={{ mt: 0.5, bgcolor: alpha(serviceColor, 0.08) }} />
          </Box>
          <Skeleton
            variant="rounded"
            width={58}
            height={34}
            sx={{ borderRadius: 999, bgcolor: alpha(serviceColor, 0.12) }}
          />
        </Box>

        <Box
          sx={{ mt: 2, border: `1px solid ${ui.divider}`, borderRadius: '16px', backgroundColor: ui.softGray, p: 2 }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center" flexWrap="wrap">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Skeleton variant="text" width="42%" height={24} sx={{ bgcolor: alpha(serviceColor, 0.1) }} />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Skeleton
                  variant="rounded"
                  width={86}
                  height={24}
                  sx={{ borderRadius: '8px', bgcolor: alpha(serviceColor, 0.08) }}
                />
                <Skeleton
                  variant="rounded"
                  width={112}
                  height={24}
                  sx={{ borderRadius: '8px', bgcolor: alpha(serviceColor, 0.08) }}
                />
              </Stack>
            </Box>
            <Skeleton
              variant="rounded"
              width={112}
              height={30}
              sx={{ borderRadius: '8px', bgcolor: alpha(serviceColor, 0.08) }}
            />
            <Skeleton
              variant="rounded"
              width={34}
              height={30}
              sx={{ borderRadius: '8px', bgcolor: alpha(serviceColor, 0.08) }}
            />
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Box sx={queryAdderTwoColumnGridSx}>
              {topicLinkingSkeletonFieldKeys.map((skeletonKey) => (
                <QueryAdderSkeletonField key={skeletonKey} ui={ui} />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function QueryAdderLayoutSkeleton({
  ui,
  detailRows = 8,
  materialRows = 3,
}: Readonly<QueryAdderLayoutSkeletonProps>) {
  const detailFields = getSkeletonFieldKeys('detail-skeleton', detailRows);
  const materialFields = getSkeletonFieldKeys('material-skeleton', materialRows);

  return (
    <Stack spacing={3} aria-busy="true" aria-label="Loading linked service details">
      <QueryAdderTopicLinkingSkeleton ui={ui} />

      <SectionCard title="Details" icon={<Skeleton variant="circular" width={20} height={20} />} ui={ui}>
        <Box sx={queryAdderTwoColumnGridSx}>
          {detailFields.map((skeletonKey) => (
            <QueryAdderSkeletonField key={skeletonKey} ui={ui} />
          ))}
        </Box>
      </SectionCard>

      <SectionCard title="Working Materials" icon={<Skeleton variant="circular" width={20} height={20} />} ui={ui}>
        <Stack spacing={2}>
          {materialFields.map((skeletonKey, index) => (
            <Skeleton
              key={skeletonKey}
              variant="rounded"
              width="100%"
              height={index === materialFields.length - 1 ? 88 : 48}
              sx={{ borderRadius: '10px', bgcolor: alpha(ui.main, 0.06) }}
            />
          ))}
        </Stack>
      </SectionCard>
    </Stack>
  );
}

export function QueryAdderStickyHeader({
  title,
  infoText,
  icon,
  ui,
  onClose,
  onSubmit,
  onCancel,
  onDelete,
  isUpdatingItem = false,
  isUpdatingRow = false,
  primaryColor,
  primaryTextColor,
  titleSx,
  iconCardSx,
  containerSx,
  topBorderColor,
}: Readonly<QueryAdderStickyHeaderProps>) {
  const theme = useTheme();
  const primaryActionColor = primaryColor || theme.palette.primary.main;
  const resolvedCancel = onCancel || onClose;
  const headerAccentColor = topBorderColor || ui.main;

  return (
    <Box
      sx={{
        ...adderModalHeaderSx,
        ...containerSx,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: { xs: 2, sm: 3.5 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ width: '100%', py: 0 }}
        >
          <Stack direction="row" alignItems="center" sx={adderHeaderTitleGroupSx}>
            <Box
              sx={{
                ...adderHeaderIconTileSx(headerAccentColor),
                ...iconCardSx,
              }}
            >
              {icon}
            </Box>
            <Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography
                  data-cy="queryAdder-title"
                  sx={{
                    ...adderHeaderTitleSx,
                    ...titleSx,
                  }}
                >
                  {title}
                </Typography>
                <HoverInfo text={infoText} ui={ui} />
              </Stack>
            </Stack>
          </Stack>

          {onSubmit ? (
            <Stack direction="row" sx={adderHeaderActionsSx}>
              {onDelete ? (
                <Button
                  variant="outlined"
                  onClick={onDelete}
                  startIcon={<DeleteOutlineIcon />}
                  sx={{
                    ...adderCancelButtonSx,
                    color: '#DC2626',
                    borderColor: '#F3B4B4',
                    '&:hover': {
                      borderColor: '#E57373',
                      backgroundColor: '#FFF5F5',
                    },
                  }}
                  data-cy="delete-button-QueryAdder"
                >
                  Delete
                </Button>
              ) : null}
              <Button
                variant="outlined"
                onClick={resolvedCancel}
                startIcon={<CloseIcon />}
                sx={{
                  ...adderCancelButtonSx,
                }}
                data-cy="cancel-button-QueryAdder"
              >
                Cancel
              </Button>
              <LoadingButton
                loading={isUpdatingItem}
                variant="contained"
                onClick={onSubmit}
                startIcon={isUpdatingRow ? <SaveIcon /> : <AddRoundedIcon />}
                sx={{
                  ...adderPrimaryActionButtonSx,
                  backgroundColor: primaryActionColor,
                  color: primaryTextColor || theme.palette.primary.contrastText,
                  '&:hover': { backgroundColor: '#155BB5' },
                }}
                data-cy={isUpdatingRow ? 'update-button-QueryAdder' : 'submit-button-QueryAdder'}
              >
                {isUpdatingRow ? 'Save' : 'Add'}
              </LoadingButton>
            </Stack>
          ) : (
            <IconButton
              aria-label="Close"
              onClick={resolvedCancel}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                border: `1px solid ${ui.actionBorder}`,
                color: ui.muted,
                backgroundColor: ui.white,
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export function QueryAdderFooterActions({
  onSubmit,
  onCancel,
  onDelete,
  isUpdatingItem = false,
  isUpdatingRow = false,
  ui,
  primaryColor,
  primaryTextColor,
}: Readonly<QueryAdderFooterActionsProps>) {
  const primaryActionColor = primaryColor || ui.actionPrimary || ui.main;
  const cancelTextColor = ui.main;
  const cancelHoverColor = '#FAFAFA';

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        borderTop: `1px solid ${ui.headerBorder}`,
        backgroundColor: ui.white,
        boxShadow: '0 -6px 18px rgba(15, 23, 42, 0.05)',
        px: 3,
        py: 2,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} flexWrap="wrap">
        <Box>
          {onDelete ? (
            <Button
              variant="outlined"
              onClick={onDelete}
              startIcon={<DeleteOutlineIcon />}
              sx={{
                ...queryAdderFooterActionSx,
                color: '#DC2626',
                borderColor: '#F3B4B4',
                backgroundColor: ui.white,
                '&:hover': {
                  borderColor: '#E57373',
                  backgroundColor: '#FFF5F5',
                },
              }}
              data-cy="delete-button-QueryAdder"
            >
              Delete
            </Button>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end" flexWrap="wrap">
          <Button
            variant="outlined"
            onClick={onCancel}
            startIcon={<CloseIcon />}
            sx={{
              ...queryAdderFooterActionSx,
              fontWeight: 600,
              borderColor: ui.actionBorder,
              color: cancelTextColor,
              backgroundColor: ui.white,
              '&:hover': {
                borderColor: ui.actionBorder,
                backgroundColor: cancelHoverColor,
              },
            }}
            data-cy="cancel-button-QueryAdder"
          >
            Cancel
          </Button>
          <LoadingButton
            loading={isUpdatingItem}
            variant="contained"
            onClick={onSubmit}
            startIcon={isUpdatingRow ? <SaveIcon /> : <AddRoundedIcon />}
            sx={{
              ...queryAdderFooterActionSx,
              backgroundColor: primaryActionColor,
              color: primaryTextColor || ui.white,
              '&:hover': { backgroundColor: primaryActionColor },
            }}
            data-cy={isUpdatingRow ? 'update-button-QueryAdder' : 'submit-button-QueryAdder'}
          >
            {isUpdatingRow ? 'Save' : 'Add'}
          </LoadingButton>
        </Stack>
      </Stack>
    </Box>
  );
}

export function QueryAdderIconField({
  icon,
  children,
  color,
  backgroundColor,
  borderColor,
  iconOffsetTop = 41,
}: Readonly<QueryAdderIconFieldProps>) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        '& .MuiInputBase-root': {
          pl: 5.5,
        },
        '& .MuiAutocomplete-inputRoot': {
          pl: '40px !important',
        },
        '& .MuiAutocomplete-input': {
          pl: '0 !important',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 14,
          top: iconOffsetTop,
          zIndex: 1,
          width: 24,
          height: 24,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          backgroundColor,
          border: borderColor ? `1px solid ${borderColor}` : undefined,
          pointerEvents: 'none',
        }}
      >
        {icon}
      </Box>
      {children}
    </Box>
  );
}

export function renderQueryAdderCell({
  col,
  fields,
  dropdownTable,
  callback,
  currMonth,
  requiredInputs,
  errorCols,
  eventHandler,
  files,
  handleDrag,
  handleDrop,
  isDragActive,
  isUpdatingRow,
  hideLabel = false,
  labelText,
  fullWidthContainer = false,
  placeholderText,
  sectionEyebrowSx,
  sectionSubtitleSx,
  sectionTitleText,
  fileRenderMode = 'full',
  liveLinkRenderMode = 'full',
}: Readonly<RenderQueryAdderCellArgs>) {
  return (
    <AnyQueryAdderCell
      key={col}
      col={col}
      dropdownTable={dropdownTable}
      fields={fields}
      callback={callback}
      defaultMonth={currMonth}
      required={requiredInputs.includes(col)}
      error={errorCols.includes(col)}
      eventHandler={eventHandler}
      files={files}
      handleDrag={handleDrag}
      handleDrop={handleDrop}
      isDragActive={isDragActive}
      isUpdating={isUpdatingRow}
      hideLabel={hideLabel}
      labelText={labelText}
      fullWidthContainer={fullWidthContainer}
      placeholderText={placeholderText}
      sectionEyebrowSx={sectionEyebrowSx}
      sectionSubtitleSx={sectionSubtitleSx}
      sectionTitleText={sectionTitleText}
      fileRenderMode={fileRenderMode}
      liveLinkRenderMode={liveLinkRenderMode}
    />
  );
}
